import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { subscribeToPublicEvents } from "../firebase/services";
import { useAuth } from "../hooks/useAuth";
import { formatDateRange } from "../utils/formatDateRange";
import logo from "../assets/logo.png";

export default function BrowseEvents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = subscribeToPublicEvents((evts) => {
      setEvents(evts);
      setLoading(false);
    });
    return unsub;
  }, []);

  const activeEvents = events.filter(e => e.status === "active");
  const completedEvents = events.filter(e => e.status !== "active");

  const goToEvent = (event) => {
    if (user && event.createdBy === user.uid) {
      navigate(`/event/${event.id}`);
    } else {
      navigate(`/public/${event.id}`);
    }
  };

  return (
    <div className="min-h-screen p-5 lg:max-w-5xl lg:mx-auto">
      <Link to="/" className="text-gray-600 text-sm no-underline hover:text-gray-400">← Home</Link>

      <div className="text-center my-4">
        <img src={logo} alt="Joy of HEMA" className="w-80 mx-auto" />
        <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-sm font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
      </div>

      <h1 className="font-display text-xl font-bold mt-3 mb-1">Browse Events</h1>
      <p className="text-gray-600 text-xs mb-5">Active and upcoming events open to the public.</p>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="flex gap-1">
            {[0, 1, 2].map(i => (
              <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-600 pulse-dot" style={{ animationDelay: `${i * 0.2}s` }} />
            ))}
          </div>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-dark-700 border border-dark-500 rounded-xl p-6 text-center">
          <p className="text-gray-500 text-sm">No public events right now</p>
        </div>
      ) : (
        <>
          {activeEvents.length > 0 && (
            <div className="mb-4">
              {activeEvents
                .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                .map(evt => (
                  <EventCard key={evt.id} event={evt} onClick={() => goToEvent(evt)} />
                ))}
            </div>
          )}
          {completedEvents.length > 0 && (
            <div>
              <div className="text-xs font-bold text-gray-600 tracking-widest mb-2">COMPLETED</div>
              {completedEvents
                .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                .map(evt => (
                  <EventCard key={evt.id} event={evt} onClick={() => goToEvent(evt)} />
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EventCard({ event, onClick }) {
  const typeLabels = { tournament: "Tournament", seminar: "Judging Seminar", training: "Training" };
  const dateRange = event.startDate ? formatDateRange(event.startDate, event.endDate) : "";

  return (
    <button onClick={onClick}
      className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-4 mb-2 cursor-pointer hover:border-gray-600 transition-colors">
      <div className="flex items-center justify-between mb-1">
        <div className="font-display text-base font-bold text-white">{event.name}</div>
        <span className="text-gray-700 text-lg">→</span>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-600">
        <span>{typeLabels[event.type] || event.type}</span>
        {dateRange && <span>{dateRange}</span>}
        {event.location && <span>{event.location}</span>}
      </div>
      {event.type === "tournament" && event.disciplines?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {event.disciplines.map(d => (
            <span key={d} className="px-2 py-0.5 bg-dark-800 border border-dark-500 rounded text-[10px] text-gray-500">{d}</span>
          ))}
        </div>
      )}
    </button>
  );
}
